import { useState, useEffect, useRef, useCallback } from 'react';

const logos = ['/zain.svg', '/stc.png', '/aramco11.png'];

// Draw random static noise on canvas
function drawStatic(canvas: HTMLCanvasElement, intensity: number) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const { width, height } = canvas;
  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const v = Math.random() < intensity ? 255 : 0;
    data[i] = v;
    data[i + 1] = v;
    data[i + 2] = v;
    data[i + 3] = Math.floor(Math.random() * 200 + 55);
  }
  ctx.putImageData(imageData, 0, 0);
}

export default function BrandGlitch() {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<'visible' | 'static' | 'entering'>('visible');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const staticFrameRef = useRef(0);

  const stopStatic = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  }, []);

  const animateStatic = useCallback((endCb: () => void) => {
    const canvas = canvasRef.current;
    if (!canvas) { endCb(); return; }
    staticFrameRef.current = 0;
    const totalFrames = 18; // ~300ms at 60fps
    const loop = () => {
      staticFrameRef.current++;
      const progress = staticFrameRef.current / totalFrames;
      const intensity = progress < 0.5
        ? 0.15 + progress * 0.7           // ramp up
        : 0.85 - (progress - 0.5) * 1.4; // ramp down
      drawStatic(canvas, Math.max(0, intensity));
      if (staticFrameRef.current < totalFrames) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        stopStatic();
        endCb();
      }
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [stopStatic]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase('static');
      animateStatic(() => {
        setIndex((prev) => (prev + 1) % logos.length);
        setPhase('entering');
        setTimeout(() => setPhase('visible'), 200);
      });
    }, 3500);
    return () => { clearInterval(interval); stopStatic(); };
  }, [animateStatic, stopStatic]);

  const imgStyle: React.CSSProperties = {
    transition: phase === 'entering' ? 'opacity 0.2s ease-out, filter 0.2s ease-out' : 'none',
    opacity: phase === 'visible' ? 1 : phase === 'entering' ? 1 : 0,
    filter: phase === 'entering'
      ? 'brightness(1) blur(0px)'
      : phase === 'static'
      ? 'brightness(3) blur(2px)'
      : 'drop-shadow(0 0 8px rgba(255,255,255,0.6))',
  };

  return (
    <div className="flex items-center gap-6 mb-8">
      <span className="text-5xl lg:text-7xl font-black text-white">مساحة</span>
      <div className="w-32 h-20 lg:w-48 lg:h-28 flex items-center justify-center relative">
        <img
          src={logos[index]}
          alt="شعار الشريك"
          className="max-w-full max-h-full object-contain"
          style={imgStyle}
        />
        {/* Static noise overlay */}
        <canvas
          ref={canvasRef}
          width={192}
          height={112}
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ mixBlendMode: 'screen', opacity: phase === 'static' ? 1 : 0 }}
        />
        {/* Horizontal scan-line sweep during static */}
        {phase === 'static' && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.04) 2px, rgba(255,255,255,0.04) 4px)',
            }}
          />
        )}
      </div>
    </div>
  );
}
